"use client";
import React, { useEffect, useState } from "react";
import SharedButton from "shared-web/components/common/Button/Button";
import Link, { type LinkHref, type LinkProps } from "src/components/common/Link/Link";
import { useFormState } from "src/components/form/Form/FormContext";
import type { ControlSize, ThemeColor } from "src/lib/FrontendTypes";
import { useResolvedHref } from "src/lib/Navigation";

interface BaseButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  color?: ThemeColor;
  variant?: "bare" | "normal" | "outline";

  label: string;
  size?: ControlSize;
  /** `pill` rounds a text button's ends; `circle` makes an icon button round. */
  shape?: "default" | "pill" | "circle";
  backgroundColor?: string;
  href?: string;

  ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;

  loading?: boolean;
  success?: boolean;

  noDisabledStyle?: boolean;

  ripple?: boolean;
}

export type ButtonProps = Omit<BaseButtonProps, "href"> &
  // The button's own `variant` styles a link button too; the Link never sees it.
  (
    | (Omit<LinkProps, "href" | "variant"> & {
        href: LinkHref;
      })
    | (Omit<BaseButtonProps, "href"> & {
        href?: unknown;
      })
  );

const Button = (props: ButtonProps) => {
  const [isNavigating, setIsNavigating] = useState(false);
  // The spinner runs until the destination is on screen, not just until the URL changes.
  const resolvedHref = useResolvedHref();
  // Reset whenever the shown page changes.
  useEffect(() => {
    void resolvedHref;
    setIsNavigating(false);
  }, [resolvedHref]);
  const formValid = useFormState((state) => state.isValid || state.submitCount === 0);
  const disabled =
    props.disabled || (formValid === false && (props.type === "submit" || props.type === "reset"));
  return (
    <SharedButton
      {...props}
      as={props.href ? Link : undefined}
      disabled={disabled}
      loading={props.loading || isNavigating}
      {...(props.href && !disabled && !props.loading && !props.success && !isNavigating
        ? { onNavigated: () => setIsNavigating(true) }
        : {})}
    />
  );
};
export default React.memo(Button);
